import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Store } from '@ngxs/store';
import { ModalController } from '@ionic/angular';
import { FormBuilder, FormGroup } from '@angular/forms';

import { SharedObject, ProductionAreaOverride } from '../../../models';
import {
  CreateOrUpdateProductionAreaOverride,
  DeleteProductionAreaOverride
} from '../../../store/pos-manager';

export interface IProductionAreaOverrideModal {
  productionAreas: SharedObject[];
  selectedProductionArea?: SharedObject;
  coreSelectedProductionArea?: SharedObject;
  overrides: ProductionAreaOverride[];
  foodId: number;
  posLocationId: number;
}

@Component({
  selector: 'pos-production-area-override',
  templateUrl: './production-area-override.modal.html',
  styleUrls: ['./production-area-override.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductionAreaOverrideModal implements OnInit {
  @Input() productionAreas: SharedObject[];
  @Input() selectedProductionArea?: SharedObject;
  @Input() coreSelectedProductionArea?: SharedObject;
  @Input() overrides: ProductionAreaOverride[];
  @Input() foodId: number;
  @Input() posLocationId: number;

  form: FormGroup;
  private readonly noneOptionId = -1;
  originalProductionAreaId: number;

  constructor(
    private readonly store: Store,
    private readonly modalController: ModalController,
    private readonly fb: FormBuilder
  ) {}

  ngOnInit() {
    // Original production area is the one that is selected in the core
    this.originalProductionAreaId =
      this.coreSelectedProductionArea?.id || this.noneOptionId;

    this.form = this.fb.group({
      selectedArea: this.selectedProductionArea?.id || this.noneOptionId
    });
    this.productionAreas = [
      {
        id: this.noneOptionId,
        name: 'None',
        type: 'production_area'
      } as SharedObject,
      ...this.productionAreas
    ];
  }

  async cancel() {
    await this.modalController.dismiss();
  }

  async save() {
    const selectedAreaId = this.form.get('selectedArea').value;
    const currentSelection =
      this.selectedProductionArea?.id || this.noneOptionId;

    // If selection hasn't changed, just close the modal
    if (currentSelection === selectedAreaId) {
      await this.modalController.dismiss();
      return;
    }

    const existingOverride = this.overrides.find(
      (area) => area.food_id === this.foodId
    );

    // Check if we're reverting to the original production area
    const isRestoringDefaultArea =
      existingOverride?.id && this.originalProductionAreaId === selectedAreaId;

    if (isRestoringDefaultArea) {
      // Delete the override when returning to original setting
      this.store.dispatch(
        new DeleteProductionAreaOverride(existingOverride.id)
      );
    } else {
      // Create or update the production area override
      const payload: ProductionAreaOverride = {
        id: existingOverride?.id,
        food_id: this.foodId,
        production_area_id:
          selectedAreaId === this.noneOptionId ? null : selectedAreaId,
        pos_location_id: this.posLocationId
      };

      this.store.dispatch(new CreateOrUpdateProductionAreaOverride(payload));
    }

    await this.modalController.dismiss();
  }
}
